import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

//import material ui
import {
  Button,
  Card,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Box,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import SendIcon from "@mui/icons-material/Send";

//import project components
import CompanyLogo from "../../../assets/images/HeaderLogo.jpg";
import JobTreeLogo from "../../../assets/images/JobTreeLogo.jpg";
import config from "../../../config";
import { setUser } from "../../../store/userSlice";

//import third party
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "../../../api";

const ForgotPassword = () => {
  //Constants
  let navigate = useNavigate();
  const dispatch = useDispatch();

  //States
  const [emailAddress, setEmailAddress] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [sendingOtp, setSendingOtp] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [step, setStep] = useState(0);
  const [countDownTimer, setCountDownTimer] = useState("3:00");
  const [startCounter, setStartCounter] = useState(false);

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      newPassword: yup
        .string()
        .min(8, "Must be at least 8 characters")
        .max(32, "Must be at most 32 characters")
        .test("is-non-whitespace", "Password cannot be empty", (value) => {
          return /\S/.test(value || "");
        })
        .required("New Password is required")
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => value && !/\s/.test(value)
        ),
      confirmPassword: yup
        .string()
        .test("is-non-whitespace", "Password cannot be empty", (value) => {
          return /\S/.test(value || "");
        })
        .oneOf([yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm Password is required")
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => value && !/\s/.test(value)
        ),
    }),
    onSubmit: async (values) => {
      setResettingPassword(true);
      try {
        const response = await axios.post(
          "/api/Authentication/reset-password",
          {
            email: emailAddress,
            otp: otp,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          }
        );

        if (response.status === 200) {
          alert("Password reset successfully. Redirecting to login page");
          navigate("/auth");
        } else {
          alert("Failed to reset password.");
        }
      } catch (error) {
        alert("Error resetting password. Please try again.");
      } finally {
        setResettingPassword(false);
      }
    },
  });

  //Methods
  const sendOtp = () => {
    setSendingOtp(true);
    axios
      .post("/api/Authentication/reset-password/send-otp", {
        email: emailAddress,
      })
      .then((response) => {
        if (response.status === 200) {
          setStep(1);
          setSendingOtp(false);
          setStartCounter(true);
        } else {
          setSendingOtp(false);
          alert(response.data);
        }
        setOtp("");
      })
      .catch((error) => {
        setSendingOtp(false);
      });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  //UseEffects
  useEffect(() => {
    if (startCounter) {
      let tempResendDateTime = new Date().getTime() + 0.2 * 60000;

      const interval = setInterval(() => {
        var now = new Date().getTime();
        var distance = tempResendDateTime - now;
        var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        var seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCountDownTimer(`${minutes}:${seconds < 10 ? "0" : ""}${seconds}`);

        if (distance < 0) {
          setCountDownTimer("");
          setStartCounter(false);
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [startCounter]);
  return (
    <Card elevation={4}>
      <Grid container>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            paddingX: 2,
            paddingY: 5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img src={JobTreeLogo} alt="company-logo" width="70%" />
          {/* <Typography
            variant="h2"
            color="primary.dark"
            sx={{ textAlign: "center", fontWeight: 600 }}
          >
            Job Tree
          </Typography> */}
        </Grid>

        <Grid
          item
          sm={6}
          sx={{
            padding: 4,
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{ marginBottom: 3, textAlign: "center" }}
            variant="h4"
            color="primary.dark"
          >
            Password Reset
          </Typography>
          <Divider sx={{ marginY: 3 }} />
          <Stack spacing={3}>
            {step === 0 && (
              <>
                <Typography>Enter you registered email address</Typography>
                <TextField
                  type="email"
                  placeholder="email address"
                  value={emailAddress}
                  onChange={(e) => {
                    setEmailAddress(e.target.value);
                  }}
                ></TextField>
                <LoadingButton
                  variant="contained"
                  endIcon={<SendIcon />}
                  loadingPosition="end"
                  loading={sendingOtp}
                  onClick={() => {
                    sendOtp();
                    setStartCounter(false);
                  }}
                >
                  Send OTP
                </LoadingButton>
                <Divider sx={{ marginY: 3 }} />
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", marginTop: 2 }}
                >
                  <Link to="/auth" color="primary.dark">
                    Back to sign in
                  </Link>
                </Typography>
              </>
            )}
            {step === 1 && (
              <>
                <Typography>Enter OTP received on registered email</Typography>
                <TextField
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                  }}
                  type="password"
                ></TextField>
                <Box>
                  <Typography textAlign="center">{countDownTimer}</Typography>
                  <LoadingButton
                    fullWidth
                    disabled={startCounter}
                    loadingPosition="end"
                    loading={sendingOtp}
                    onClick={() => {
                      sendOtp();
                      setStartCounter(false);
                    }}
                  >
                    Resend OTP
                  </LoadingButton>
                </Box>
                <LoadingButton
                  variant="contained"
                  endIcon={<LoginIcon />}
                  loadingPosition="end"
                  loading={resettingPassword}
                  onClick={() => {
                    setResettingPassword(true);
                    if (otp === "") {
                      alert("Please enter otp");
                      setResettingPassword(false);
                      return;
                    }

                    axios
                      .post(
                        "/api/Authentication/reset-password/verify-otp",
                        {
                          email: emailAddress,
                          otp: otp,
                        }
                      )
                      .then((response) => {
                        if (response.status === 200) {
                          setStep(2);
                        } else {
                          alert(response.data);
                        }
                        setResettingPassword(false);
                      })
                      .catch((error) => {
                        setResettingPassword(false);
                      });
                  }}
                >
                  Submit
                </LoadingButton>
                <Divider sx={{ marginY: 3 }} />{" "}
                <Button
                  onClick={() => {
                    setStep(0);
                  }}
                >
                  Change Email Address
                </Button>
              </>
            )}

            {step === 2 && (
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  <Typography variant="h6">Set New Password</Typography>

                  <TextField
                    fullWidth
                    name="newPassword"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.newPassword &&
                      Boolean(formik.errors.newPassword)
                    }
                    helperText={
                      formik.touched.newPassword && formik.errors.newPassword
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <LoadingButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    loading={resettingPassword}
                  >
                    Reset Password
                  </LoadingButton>

                  <Button
                    onClick={() => {
                      navigate("/auth");
                    }}
                  >
                    Back to Sign In
                  </Button>
                </Stack>
              </form>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};

export default ForgotPassword;
